import {
  ISupplierUserProfile,
  ISupportMessage,
  IUser,
} from 'constants/interfaces';
import IconButton from '@material-ui/core/IconButton';
import {
  Avatar,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import SendIcon from '@material-ui/icons/Send';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import Toolbar from '@material-ui/core/Toolbar';
import AppBar from '@material-ui/core/AppBar';
import {Skeleton} from '@material-ui/lab';
import {TextMessage} from 'screens/Dashboard/Invoices/InvoiceChat/TextMessage';
import {PermissionsService} from 'services/PermissionsService';
import {ESupplierUserPermissions} from 'constants/index';

export function CustomerChat(props: {
  isLoading: boolean;
  isRefreshing: boolean;
  user: ISupplierUserProfile;
  customer?: IUser;
  messages: ISupportMessage[];
  onMessageSend: (text: string) => any;
  onImageSend: (image: File) => any;
}) {
  const {
    isLoading,
    isRefreshing,
    user,
    customer,
    messages,
    onMessageSend,
    onImageSend,
  } = props;
  const [messageText, setMessageText] = useState<string>('');
  const [bottomOfMessagesRef, setBottomOfMessagesRef] = useState<any>();

  const sendMessage = () => {
    if (!canWrite) return;
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage) return;
    onMessageSend(trimmedMessage);
    setMessageText('');
  };

  const handleTextChange = (event: any) => {
    // ignore when you press enter. Used as a trigger to send message
    if (event.target.value === '\n') return;
    setMessageText(event.target.value);
  };

  const handleImageUpload = (event: any) => {
    if (!canWrite) return;
    const file = event?.target?.files?.[0];
    if (!file) return;
    onImageSend(file);
  };

  const scrollToBottom = () => {
    bottomOfMessagesRef?.scrollIntoView({behavior: 'auto'});
  };

  useEffect(scrollToBottom, [isLoading, messages]);

  const compareMessageDates = (a: ISupportMessage, b: ISupportMessage) => {
    if (a.date < b.date) return -1;
    if (a.date > b.date) return 1;
    return 0;
  };

  const permissions = new PermissionsService(props.user);
  const canWrite = permissions.hasPermission(
    ESupplierUserPermissions.CUSTOMER_CHAT_WRITE,
  );

  return (
    <Box className="order-chat-container-2">
      <AppBar className="title-bar" elevation={0} variant="outlined">
        <Toolbar>
          <Avatar src={customer?.farmer.image} className={'avatar'} />
          <Box className="title">
            <Typography variant="body2" color="textPrimary">
              {customer?.farmer.name} {customer?.farmer.surname}
            </Typography>
            {customer?.farmer?.farm?.name && (
              <Typography variant="caption" color="textPrimary">
                {customer?.farmer.farm?.name}
              </Typography>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Box className="order-chat-message-container">
        {isLoading &&
          [1, 2, 3, 4, 5].map((index) => (
            <Box key={index} mb={2}>
              <Skeleton variant="rect" height={100} style={{borderRadius: 8}} />
            </Box>
          ))}
        {!isLoading &&
          messages
            .sort(compareMessageDates)
            .map(
              (message) =>
                (message.type === 'TEXT' ||
                  message.type === 'IMAGE' ||
                  message.type === 'FORM') && (
                  <TextMessage
                    key={message.id}
                    myProfileImage={user?.pictureUrl}
                    farmerProfileImage={customer?.farmer.image}
                    date={message.date}
                    messageText={message.message}
                    isSent={message.from.id === user.userId}
                    isImage={message.type === 'IMAGE'}
                  />
                ),
            )}
        <div ref={(element) => setBottomOfMessagesRef(element)} />
      </Box>
      <AppBar className="response-bar" elevation={0} variant="outlined">
        <Toolbar>
          <Avatar src={user.pictureUrl} className={'avatar'} />
          <TextField
            className="text-input"
            id="outlined-basic"
            variant="outlined"
            size="small"
            multiline
            value={messageText}
            onChange={handleTextChange}
            onKeyDown={(e) => {
              // send message when you press enter
              e.keyCode === 13 && sendMessage();
            }}
            disabled={!canWrite}
          />
          <IconButton
            component="label"
            className="photo-upload-button"
            size="small"
            disabled={!canWrite}
          >
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageUpload}
            />
            <PhotoCameraIcon />
          </IconButton>
          {isRefreshing ? (
            <CircularProgress color={'primary'} size={20} thickness={6} />
          ) : (
            <IconButton
              className="send-button"
              size="small"
              onClick={sendMessage}
              disabled={!canWrite}
            >
              <SendIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  );
}
